// Client-side Jinja highlighting + fake-data fill for the email-template preview.
// We do NOT run real jinja2 in the browser: the send-Lambdas only ever pass a
// flat dict and use `{{ var }}` interpolation, so a tokenizer that classifies
// each delimited run against the known-tag set covers 100% of real usage and
// gives precise blue/red control.
//
// The vocabulary is a PARAMETER, never an import. It is owned by the
// infrastructure shared layer and fetched at runtime, so this app holds no copy
// that can drift from what the senders emit. A null vocabulary means "we don't
// know" -- every function below goes inert rather than guessing, because a tag
// wrongly shown valid renders blank in a real creator's email.

import {
  variableNamesFor,
  type EmailTemplateVocabulary,
} from "../api/emailTemplateVars";

const BLUE = "#2b6cb0"; // delivered to this template
const RED = "#e53e3e"; // not delivered here (wrong lane, or not a variable)

export type TagKind = "known" | "wrong-template" | "unknown";

export type Vocab = {
  /** names delivered to the template being edited */
  allowed: Set<string>;
  /** every name the catalog knows, across all templates */
  all: Set<string>;
  fakes: Record<string, string>;
  templates: Record<string, string[]>;
} | null;

export function buildVocab(
  catalog: EmailTemplateVocabulary | null,
  templateName: string | null,
): Vocab {
  if (!catalog) return null;
  // Guard the shape, not just presence: a 200 with an unexpected body (proxy
  // error page, truncated JSON) must fall back to the inert null-vocab path
  // rather than throw during render. An EMPTY catalog (empty arrays/object)
  // is still valid and must be honored — everything reds out correctly.
  if (
    !Array.isArray(catalog.variables) ||
    typeof catalog.templates !== "object" ||
    catalog.templates === null ||
    !Array.isArray(catalog.base)
  ) {
    return null;
  }
  return {
    allowed: new Set(variableNamesFor(catalog, templateName)),
    all: new Set(catalog.variables.map((v) => v.name)),
    fakes: Object.fromEntries(catalog.variables.map((v) => [v.name, v.fake])),
    templates: catalog.templates,
  };
}

/** Templates that DO deliver `name` — used to explain a wrong-template tag. */
export function deliveredTo(name: string, vocab: Vocab): string[] {
  if (!vocab) return [];
  return Object.entries(vocab.templates)
    .filter(([, names]) => names.includes(name))
    .map(([template]) => template);
}

// A complete `{{ ident }}`.
const KNOWN_TAG_RE = /^\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}$/;

// Split pattern: complete `{{…}}` / `{%…%}` first (longest, so a well-formed tag
// is one piece), then bare delimiters so an unbalanced `{{`/`}}`/`{%`/`%}` is
// still caught and flagged. Capturing group keeps the delimiters in String.split.
const SCAN_RE = /(\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}|\{\{|\}\}|\{%|%\})/g;
// Same alternatives, anchored, to test whether a split piece is jinja.
const PIECE_RE = /^(?:\{\{[\s\S]*?\}\}|\{%[\s\S]*?%\}|\{\{|\}\}|\{%|%\})$/;

/**
 * Classify a jinja piece against the vocabulary.
 *
 * `wrong-template` is the interesting state: a real variable used in a template
 * that never receives it. It renders as an empty string in production, so it
 * must NOT read as valid.
 */
export function classifyTag(raw: string, vocab: Vocab): TagKind {
  if (!vocab) return "unknown";
  const m = raw.match(KNOWN_TAG_RE);
  if (!m) return "unknown";
  const name = m[1];
  if (vocab.allowed.has(name)) return "known";
  if (vocab.all.has(name)) return "wrong-template";
  return "unknown";
}

/**
 * Extract the identifier from a complete `{{ ident }}` tag value, e.g. for
 * building a hover title on a classified segment. Returns null for anything
 * that isn't a well-formed single-variable tag (malformed/unbalanced pieces).
 */
export function tagName(raw: string): string | null {
  const m = raw.match(KNOWN_TAG_RE);
  return m ? m[1] : null;
}

export interface JinjaSegment {
  value: string;
  kind: "text" | TagKind;
}

/**
 * Split a PLAIN-TEXT string (e.g. an email subject) into segments classified for
 * inline highlighting. Callers render each segment as a span. With a null vocab
 * every segment is plain text — we make no claims we can't back.
 */
export function segmentJinja(text: string, vocab: Vocab): JinjaSegment[] {
  const pieces = text.split(SCAN_RE).filter((p) => p !== "");
  if (!vocab) return pieces.map((value) => ({ value, kind: "text" as const }));
  return pieces.map((p) =>
    PIECE_RE.test(p)
      ? { value: p, kind: classifyTag(p, vocab) }
      : { value: p, kind: "text" as const },
  );
}

export function colorFor(kind: "text" | TagKind): string | undefined {
  if (kind === "known") return BLUE;
  if (kind === "wrong-template" || kind === "unknown") return RED;
  return undefined;
}

/**
 * Return the HTML with Jinja tags in TEXT NODES wrapped in colored spans.
 * Attributes are left untouched (their raw `{{ }}` stays intact but unhighlighted)
 * so we never break an href/src. Building spans as DOM elements auto-escapes.
 */
export function highlightHtml(html: string, vocab: Vocab): string {
  if (!vocab) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const targets: Text[] = [];
  let n = walker.nextNode();
  while (n) {
    const parentTag = n.parentNode?.nodeName;
    // Skip <style>/<script> text so we never inject spans into CSS/JS source.
    if (
      n.nodeValue &&
      parentTag !== "STYLE" &&
      parentTag !== "SCRIPT" &&
      (n.nodeValue.includes("{{") || n.nodeValue.includes("{%"))
    ) {
      targets.push(n as Text);
    }
    n = walker.nextNode();
  }

  for (const node of targets) {
    const pieces = node.nodeValue!.split(SCAN_RE);
    const frag = doc.createDocumentFragment();
    for (const piece of pieces) {
      if (piece === "") continue;
      if (PIECE_RE.test(piece)) {
        const kind = classifyTag(piece, vocab);
        const span = doc.createElement("span");
        span.style.color = colorFor(kind)!;
        if (kind !== "known") span.style.fontWeight = "600";
        span.textContent = piece;
        frag.appendChild(span);
      } else {
        frag.appendChild(doc.createTextNode(piece));
      }
    }
    node.parentNode?.replaceChild(frag, node);
  }

  return doc.documentElement.outerHTML;
}

/**
 * Replace every `{{ tag }}` DELIVERED TO THIS TEMPLATE with its fake value (in
 * text AND attributes, so links resolve). Anything else is left verbatim —
 * including real variables from another lane, which would render blank in a
 * real send.
 */
export function renderWithFakeData(html: string, vocab: Vocab): string {
  if (!vocab) return html;
  return html.replace(
    /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g,
    (m, name) => (vocab.allowed.has(name) ? vocab.fakes[name] : m),
  );
}
